import type { TextFieldVariants } from '@mui/material'
import { InputAdornment } from '@mui/material'
import { TextField } from 'components/@extended/TextField'
import React from 'react'
import { NumericFormat } from 'react-number-format'

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void
  name: string
  decimalPlaces: number
  pill?: boolean
  shrink?: boolean
}

const NumericFormatCustom = function NumericFormatCustom({
  ref,
  ...props
}: CustomProps & { ref?: React.RefObject<HTMLInputElement | null> }) {
  const { onChange, decimalPlaces, pill, shrink, ...other } = props
  const { allowEmptyFormatting, ...safeOther } = other as any

  return (
    <NumericFormat
      {...safeOther}
      getInputRef={ref}
      onValueChange={(values: { value: string }) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        })
      }}
      thousandSeparator
      valueIsNumericString
      allowNegative={false}
      decimalScale={decimalPlaces}
      fixedDecimalScale={true}
    />
  )
}

interface PercentageFieldProps {
  id?: string
  label?: string
  value: string
  onChange: (value: string) => void
  name: string
  decimalPlaces?: number
  style?: React.CSSProperties // Add the optional style property
  variant?: TextFieldVariants
  pill?: boolean
  shrink?: boolean
  disabled?: boolean
  sx?: object
  allowEmpty?: boolean
}

const PercentageField: React.FC<PercentageFieldProps> = ({
  id,
  label,
  value,
  onChange,
  name,
  decimalPlaces = 2,
  style, // Destructure the style prop
  variant = 'standard',
  pill = false,
  shrink = false,
  disabled = false,
  sx = {}, // Initialize sx prop with an empty object
  allowEmpty = false,
}) => {
  const handleChange = (event: { target: { name: string; value: string } }) => {
    // Convert the percentage back to a decimal
    if (event.target.value === '') {
      onChange('')
    } else {
      const decimalValue = (Number.parseFloat(event.target.value) / 100).toString()
      onChange(decimalValue)
    }
  }

  // Convert the decimal to a percentage for display
  const displayValue = value === '' || value === null ? '' : (Number.parseFloat(value) * 100).toFixed(decimalPlaces)

  return (
    <TextField
      id={id}
      fullWidth
      label={label}
      value={displayValue}
      onChange={handleChange}
      name={name}
      variant={variant}
      pill={pill}
      shrink={shrink}
      disabled={disabled}
      InputProps={{
        inputComponent: NumericFormatCustom as any,
        inputProps: {
          decimalPlaces: decimalPlaces,
          allowEmptyFormatting: allowEmpty,
        },
        endAdornment: <InputAdornment position="end">%</InputAdornment>,
        sx: {
          '& input': {
            textAlign: 'right',
            paddingRight: 1,
          },
        },
      }}
      style={style}
      sx={sx}
    />
  )
}

export default PercentageField
